
















import { Component, Inject, Mixins } from "vue-property-decorator";
import { IUploadFile } from "@/models/uploadFiles";
import { AxiosError } from "axios";
import { DataModules } from "@/store/modules/data";
import { IBasket } from "@/models/baskets";
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import Context from "@/mixins/context";
import CustomFields from "@/components/app/global/customFields/customFields.vue";

@Component({
  components: {
    CustomFields
  }
})
export default class BasketFields extends Mixins(Context) {
  // Refs

  public declare $refs: {
    customFields: CustomFields;
  };

  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketProviderData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  // Data

  public basketId: IBasket["id"] = "";

  // Computed

  public get $basketData() {
    return this.$basketProviderData();
  }

  // Methods

  public async saveBasketFieldsData() {
    await this.tryCustomImageUpload();
    const {
      basketForm,
      basketFormClone,
      isNewBasket,
      basketIsComplete
    } = this.$basketData;
    if (basketIsComplete) return; // Abort if basket already converted
    if (this.$_.isEqual(basketForm, basketFormClone)) return;
    await this.$basketMethods.updateBasketData(basketForm, this.basketId);
    if (!isNewBasket) await this.$basketMethods.getBasket(true, this.basketId);
  }

  public async tryCustomImageUpload() {
    if (!this.$basketData.hasUnsavedImages) return;
    await this.$refs.customFields.uploadCustomImages(this.isGuest);
    this.$basketMethods.saveCustomImages([]);
  }

  public handleImageUploadError(error: AxiosError) {
    if (!this.$store.getters["api/is"](error, 422)) return;
    if (!error?.response?.config?.url?.includes("/image")) return;
    const params = error?.response?.config?.url?.split("/");
    const fieldId = params[`${params.length - 2}`];
    // Remove corresponding custom image
    this.$basketMethods.saveCustomImages(
      this.$_.filter(
        this.$basketData.customImages,
        image => image.id !== fieldId
      )
    );
  }

  // Life-cycle methods

  /**
   * @name created
   * @desc When this component is created we take a local copy of basketId, as
   * when beforeDestroy() is called `$basketData.basketId` will be seen as 'new'
   **/

  public created() {
    this.basketId = this.$basketData.basketId;
  }

  public get props() {
    return {
      brandId: this.$basketData.basketBrandId,
      dataModule: DataModules.CUSTOM_FIELDS_BASKET,
      externalCustomImages: this.$basketData.customImages,
      isProcessing: this.$basketData.isProcessing,
      value: this.$basketData.basketForm
    };
  }

  public get listeners() {
    return {
      ["input"]: value => (this.$basketData.basketForm = value),
      ["update:externalCustomImages"]: (customImages: IUploadFile[]) =>
        this.$basketMethods.saveCustomImages(customImages)
    };
  }

  public async beforeDestroy() {
    try {
      this.saveBasketFieldsData();
    } catch (error) {
      // Silently handle image upload errors
      await this.handleImageUploadError(error as AxiosError);
    }
  }
}
